"use client"

import {
  Fragment,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { Dialog, Transition } from "@headlessui/react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Button, Text } from "@medusajs/ui"
import { XMark } from "@medusajs/icons"

import useToggleState from "@lib/hooks/use-toggle-state"
import { getSearchContext } from "@lib/search/session"
import { sendSearchEvents } from "@lib/search/events"

import { SORT_OPTIONS, SortOptions } from "./sort-products"

type CategoryOption = {
  id: string
  name: string
  slug?: string
  handle?: string
}

type BrandOption = { name: string; slug: string }

type CollectionOption = { name: string; slug?: string; id?: string }

type RefinementListProps = {
  sortBy: SortOptions
  search?: boolean
  categories?: CategoryOption[]
  brands?: BrandOption[]
  collections?: CollectionOption[]
  pageContext?: "shop_page" | "category_page" | "brand_page" | "collection_page"
  "data-testid"?: string
}

const MAX_VISIBLE_BRANDS = 12
const MAX_VISIBLE_CATEGORIES = 12
const MAX_VISIBLE_COLLECTIONS = 10

const normalizeListParam = (params: URLSearchParams, key: string) => {
  const all = params.getAll(key)
  if (all.length) return all
  const raw = params.get(key)
  if (!raw) return []
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
}

const normalizeValue = (value: string) => value.trim().toLowerCase()

const RefinementList = ({
  sortBy,
  categories,
  brands,
  collections,
  pageContext,
  "data-testid": dataTestId,
}: RefinementListProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const filterDrawer = useToggleState(false)

  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [brandQuery, setBrandQuery] = useState("")
  const [showAllBrands, setShowAllBrands] = useState(false)
  const [showAllCategories, setShowAllCategories] = useState(false)
  const [showAllCollections, setShowAllCollections] = useState(false)

  const updateQueryParam = (name: string, value?: string | null) => {
    const params = new URLSearchParams(searchParams)

    if (!value) {
      params.delete(name)
    } else {
      params.set(name, value)
    }

    params.delete("page")
    params.delete("cursor")

    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname)

    if (name === "sortBy" && pageContext) {
      const { sessionId } = getSearchContext()
      void sendSearchEvents({
        sorts: [
          {
            sessionId,
            query: null,
            oldSort: sortBy,
            newSort: value,
            pageContext,
          },
        ],
      })
    }
  }

  const toggleMultiParam = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams)
      const current = normalizeListParam(params, name)
      params.delete(name)
      if (current.includes(value)) {
        current
          .filter((entry) => entry !== value)
          .forEach((entry) => params.append(name, entry))
      } else {
        current.forEach((entry) => params.append(name, entry))
        params.append(name, value)
      }
      params.delete("page")
      params.delete("cursor")
      const queryString = params.toString()
      router.push(queryString ? `${pathname}?${queryString}` : pathname)
      if (pageContext) {
        const { sessionId } = getSearchContext()
        void sendSearchEvents({
          filters: [
            {
              sessionId,
              query: null,
              filterName: name,
              filterValue: value,
              pageContext,
            },
          ],
        })
      }
    },
    [pageContext, pathname, router, searchParams]
  )

  const setBooleanParam = useCallback(
    (name: string, value: boolean) => {
      const params = new URLSearchParams(searchParams)
      if (value) {
        params.set(name, "true")
      } else {
        params.delete(name)
      }
      params.delete("page")
      params.delete("cursor")
      const queryString = params.toString()
      router.push(queryString ? `${pathname}?${queryString}` : pathname)
      if (pageContext) {
        const { sessionId } = getSearchContext()
        void sendSearchEvents({
          filters: [
            {
              sessionId,
              query: null,
              filterName: name,
              filterValue: value ? "true" : "false",
              pageContext,
            },
          ],
        })
      }
    },
    [pageContext, pathname, router, searchParams]
  )

  const applyTextParam = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams)
      const trimmed = value.trim()
      if (trimmed) {
        params.set(name, trimmed)
      } else {
        params.delete(name)
      }
      params.delete("page")
      params.delete("cursor")
      const queryString = params.toString()
      router.push(queryString ? `${pathname}?${queryString}` : pathname)
      if (pageContext) {
        const { sessionId } = getSearchContext()
        void sendSearchEvents({
          filters: [
            {
              sessionId,
              query: null,
              filterName: name,
              filterValue: trimmed,
              pageContext,
            },
          ],
        })
      }
    },
    [pageContext, pathname, router, searchParams]
  )

  const applyPriceRange = useCallback(
    (minValue: string, maxValue: string) => {
      const params = new URLSearchParams(searchParams)
      if (minValue.trim()) {
        params.set("priceMin", minValue.trim())
      } else {
        params.delete("priceMin")
        params.delete("minPrice")
      }
      if (maxValue.trim()) {
        params.set("priceMax", maxValue.trim())
      } else {
        params.delete("priceMax")
        params.delete("maxPrice")
      }
      params.delete("page")
      params.delete("cursor")
      const queryString = params.toString()
      router.push(queryString ? `${pathname}?${queryString}` : pathname)
      if (pageContext) {
        const { sessionId } = getSearchContext()
        void sendSearchEvents({
          filters: [
            {
              sessionId,
              query: null,
              filterName: "price",
              filterValue: `${minValue}-${maxValue}`,
              pageContext,
            },
          ],
        })
      }
    },
    [pageContext, pathname, router, searchParams]
  )

  const categoryLinks = Array.isArray(categories)
    ? categories
        .filter(
          (category) => category?.name && (category?.slug || category?.handle)
        )
        .map((category) => ({
          name: category.name,
          path: `/categories/${category.slug || category.handle}`,
          id: category.id,
        }))
    : []

  const brandOptions = Array.isArray(brands)
    ? brands
        .filter((brand) => brand?.name && brand?.slug)
        .map((brand) => ({ label: brand.name, value: brand.slug }))
    : []

  const collectionOptions = Array.isArray(collections)
    ? collections
        .filter(
          (collection) =>
            collection?.name && (collection?.slug || collection?.id)
        )
        .map((collection) => ({
          label: collection.name,
          value: collection.slug || String(collection.id),
        }))
    : []

  const categoryFilterOptions = Array.isArray(categories)
    ? categories
        .filter(
          (category) => category?.name && (category?.slug || category?.handle)
        )
        .map((category) => ({
          label: category.name,
          value: category.slug || category.handle || category.id,
        }))
    : []

  const selectedBrands = normalizeListParam(searchParams, "brand")
  const selectedCategories = normalizeListParam(searchParams, "category")
  const selectedCollections = normalizeListParam(searchParams, "collection")
  const selectedAvailability = normalizeListParam(
    searchParams,
    "availabilityType"
  )
  const selectedCurrency = normalizeListParam(searchParams, "currency")
  const selectedColors = normalizeListParam(searchParams, "color")
  const selectedSizes = normalizeListParam(searchParams, "size")
  const selectedMaterials = normalizeListParam(searchParams, "material")
  const inStock = searchParams.get("inStock") === "true"
  const discounted = searchParams.get("discounted") === "true"
  const priceMin =
    searchParams.get("priceMin") || searchParams.get("minPrice") || ""
  const priceMax =
    searchParams.get("priceMax") || searchParams.get("maxPrice") || ""

  const [colorInput, setColorInput] = useState(selectedColors[0] || "")
  const [sizeInput, setSizeInput] = useState(selectedSizes[0] || "")
  const [materialInput, setMaterialInput] = useState(selectedMaterials[0] || "")
  const [priceMinInput, setPriceMinInput] = useState(priceMin)
  const [priceMaxInput, setPriceMaxInput] = useState(priceMax)

  useEffect(() => {
    setColorInput(selectedColors[0] || "")
  }, [selectedColors])

  useEffect(() => {
    setSizeInput(selectedSizes[0] || "")
  }, [selectedSizes])

  useEffect(() => {
    setMaterialInput(selectedMaterials[0] || "")
  }, [selectedMaterials])

  useEffect(() => {
    setPriceMinInput(priceMin)
    setPriceMaxInput(priceMax)
  }, [priceMin, priceMax])

  const hasCategories = categoryLinks.length > 0
  const normalizedBrandQuery = normalizeValue(brandQuery)

  const filteredBrandOptions = useMemo(() => {
    if (!normalizedBrandQuery) return brandOptions
    return brandOptions.filter((brand) =>
      normalizeValue(brand.label).includes(normalizedBrandQuery)
    )
  }, [brandOptions, normalizedBrandQuery])

  useEffect(() => {
    setShowAllBrands(false)
  }, [normalizedBrandQuery])

  const visibleBrands = showAllBrands
    ? filteredBrandOptions
    : filteredBrandOptions.slice(0, MAX_VISIBLE_BRANDS)
  const visibleCategories = showAllCategories
    ? categoryFilterOptions
    : categoryFilterOptions.slice(0, MAX_VISIBLE_CATEGORIES)
  const visibleCollections = showAllCollections
    ? collectionOptions
    : collectionOptions.slice(0, MAX_VISIBLE_COLLECTIONS)

  const activeFilterCount =
    selectedBrands.length +
    selectedCategories.length +
    selectedCollections.length +
    selectedColors.length +
    selectedSizes.length +
    selectedMaterials.length +
    selectedAvailability.length +
    selectedCurrency.length +
    (inStock ? 1 : 0) +
    (discounted ? 1 : 0) +
    (priceMin || priceMax ? 1 : 0)

  const sectionTitleClass =
    "font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/50"

  const renderFilterControls = (closeDrawer?: () => void) => (
    <div className="flex flex-col gap-6">
      <section className="border-b border-black/10 pb-5">
        <p className={sectionTitleClass}>Ordenar</p>
        <select
          value={sortBy}
          onChange={(event) => {
            updateQueryParam("sortBy", event.target.value)
            if (closeDrawer) closeDrawer()
          }}
          className="mt-3 h-10 w-full border-0 border-b border-black/20 bg-transparent px-0 font-lealti-light text-sm text-black outline-none transition focus:border-black"
          data-testid={dataTestId}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </section>

      <section className="border-b border-black/10 pb-5">
        <p className={sectionTitleClass}>Rápidos</p>
        <div className="mt-3 flex flex-wrap gap-5">
          <button
            type="button"
            aria-pressed={inStock}
            onClick={() => setBooleanParam("inStock", !inStock)}
            className={`font-lealti-light text-[11px] uppercase tracking-[0.2em] transition ${
              inStock
                ? "border-b border-black pb-0.5 text-black"
                : "text-black/45 hover:text-black"
            }`}
          >
            En stock
          </button>
          <button
            type="button"
            aria-pressed={discounted}
            onClick={() => setBooleanParam("discounted", !discounted)}
            className={`font-lealti-light text-[11px] uppercase tracking-[0.2em] transition ${
              discounted
                ? "border-b border-black pb-0.5 text-black"
                : "text-black/45 hover:text-black"
            }`}
          >
            En oferta
          </button>
        </div>
      </section>

      <section className="border-b border-black/10 pb-5">
        <p className={sectionTitleClass}>Precio</p>
        <div className="mt-3 grid grid-cols-2 gap-4">
          <input
            value={priceMinInput}
            onChange={(event) => setPriceMinInput(event.target.value)}
            placeholder="Mín"
            inputMode="numeric"
            className="h-8 w-full border-0 border-b border-black/20 bg-transparent px-0 font-lealti-light text-sm text-black outline-none transition placeholder:text-black/30 focus:border-black"
          />
          <input
            value={priceMaxInput}
            onChange={(event) => setPriceMaxInput(event.target.value)}
            placeholder="Máx"
            inputMode="numeric"
            className="h-8 w-full border-0 border-b border-black/20 bg-transparent px-0 font-lealti-light text-sm text-black outline-none transition placeholder:text-black/30 focus:border-black"
          />
        </div>
        <button
          type="button"
          className="mt-4 font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/50 transition hover:text-black"
          onClick={() => {
            applyPriceRange(priceMinInput, priceMaxInput)
            closeDrawer?.()
          }}
        >
          Aplicar precio
        </button>
      </section>

      {brandOptions.length > 0 && (
        <section className="border-b border-black/10 pb-5">
          <div className="flex items-center justify-between gap-2">
            <p className={sectionTitleClass}>Marcas</p>
            {selectedBrands.length > 0 && (
              <span className="font-lealti-light text-[10px] uppercase tracking-[0.14em] text-black/40">
                {selectedBrands.length} activas
              </span>
            )}
          </div>
          <input
            value={brandQuery}
            onChange={(event) => setBrandQuery(event.target.value)}
            placeholder="Buscar marca"
            className="mt-3 h-8 w-full border-0 border-b border-black/15 bg-transparent px-0 font-lealti-light text-sm text-black outline-none transition placeholder:text-black/30 focus:border-black"
          />
          <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
            {visibleBrands.map((brand) => (
              <label
                key={brand.value}
                className="flex cursor-pointer items-center gap-2.5 py-0.5 font-lealti-light text-sm text-black/70 transition hover:text-black"
              >
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(brand.value)}
                  onChange={() => toggleMultiParam("brand", brand.value)}
                  className="h-3 w-3 shrink-0 rounded-none border-black/30 text-black focus:ring-0"
                />
                <span>{brand.label}</span>
              </label>
            ))}
            {visibleBrands.length === 0 && (
              <p className="font-lealti-light text-xs text-black/40">
                Sin coincidencias.
              </p>
            )}
          </div>
          {filteredBrandOptions.length > MAX_VISIBLE_BRANDS && (
            <button
              type="button"
              onClick={() => setShowAllBrands((prev) => !prev)}
              className="mt-3 font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/40 transition hover:text-black"
            >
              {showAllBrands ? "Ver menos" : "Ver más"}
            </button>
          )}
        </section>
      )}

      {categoryFilterOptions.length > 0 && (
        <section className="border-b border-black/10 pb-5">
          <div className="flex items-center justify-between gap-2">
            <p className={sectionTitleClass}>Categorías</p>
            {selectedCategories.length > 0 && (
              <span className="font-lealti-light text-[10px] uppercase tracking-[0.14em] text-black/40">
                {selectedCategories.length} activas
              </span>
            )}
          </div>
          <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
            {visibleCategories.map((category) => (
              <label
                key={category.value}
                className="flex cursor-pointer items-center gap-2.5 py-0.5 font-lealti-light text-sm text-black/70 transition hover:text-black"
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.value)}
                  onChange={() => toggleMultiParam("category", category.value)}
                  className="h-3 w-3 shrink-0 rounded-none border-black/30 text-black focus:ring-0"
                />
                <span>{category.label}</span>
              </label>
            ))}
          </div>
          {categoryFilterOptions.length > MAX_VISIBLE_CATEGORIES && (
            <button
              type="button"
              onClick={() => setShowAllCategories((prev) => !prev)}
              className="mt-3 font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/40 transition hover:text-black"
            >
              {showAllCategories ? "Ver menos" : "Ver más"}
            </button>
          )}
        </section>
      )}

      <section className="border-b border-black/10 pb-5">
        <button
          type="button"
          onClick={() => setAdvancedOpen((prev) => !prev)}
          className="flex w-full items-center justify-between"
        >
          <p className={sectionTitleClass}>Avanzados</p>
          <span className="font-lealti-light text-[10px] uppercase tracking-[0.18em] text-black/40 transition hover:text-black/70">
            {advancedOpen ? "Ocultar" : "Mostrar"}
          </span>
        </button>

        {advancedOpen && (
          <div className="mt-4 space-y-5">
            {collectionOptions.length > 0 && (
              <div>
                <p className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/45">
                  Colecciones
                </p>
                <div className="mt-2 max-h-48 space-y-2 overflow-y-auto pr-1">
                  {visibleCollections.map((collection) => (
                    <label
                      key={collection.value}
                      className="flex cursor-pointer items-center gap-2.5 py-0.5 font-lealti-light text-sm text-black/70 transition hover:text-black"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCollections.includes(collection.value)}
                        onChange={() =>
                          toggleMultiParam("collection", collection.value)
                        }
                        className="h-3 w-3 shrink-0 rounded-none border-black/30 text-black focus:ring-0"
                      />
                      <span>{collection.label}</span>
                    </label>
                  ))}
                </div>
                {collectionOptions.length > MAX_VISIBLE_COLLECTIONS && (
                  <button
                    type="button"
                    onClick={() => setShowAllCollections((prev) => !prev)}
                    className="mt-2 font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/40 transition hover:text-black"
                  >
                    {showAllCollections ? "Ver menos" : "Ver más"}
                  </button>
                )}
              </div>
            )}

            <div>
              <p className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/45">
                Color
              </p>
              <input
                value={colorInput}
                onChange={(event) => setColorInput(event.target.value)}
                onBlur={() => applyTextParam("color", colorInput)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    applyTextParam("color", colorInput)
                  }
                }}
                placeholder="Ej: negro"
                className="mt-2 h-8 w-full border-0 border-b border-black/15 bg-transparent px-0 font-lealti-light text-sm text-black outline-none transition placeholder:text-black/30 focus:border-black"
              />
            </div>

            <div>
              <p className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/45">
                Talle
              </p>
              <input
                value={sizeInput}
                onChange={(event) => setSizeInput(event.target.value)}
                onBlur={() => applyTextParam("size", sizeInput)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    applyTextParam("size", sizeInput)
                  }
                }}
                placeholder="Ej: M"
                className="mt-2 h-8 w-full border-0 border-b border-black/15 bg-transparent px-0 font-lealti-light text-sm text-black outline-none transition placeholder:text-black/30 focus:border-black"
              />
            </div>

            <div>
              <p className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/45">
                Material
              </p>
              <input
                value={materialInput}
                onChange={(event) => setMaterialInput(event.target.value)}
                onBlur={() => applyTextParam("material", materialInput)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    applyTextParam("material", materialInput)
                  }
                }}
                placeholder="Ej: lino"
                className="mt-2 h-8 w-full border-0 border-b border-black/15 bg-transparent px-0 font-lealti-light text-sm text-black outline-none transition placeholder:text-black/30 focus:border-black"
              />
            </div>
          </div>
        )}
      </section>
    </div>
  )

  return (
    <div className="w-full small:w-[290px] small:pr-8">
      <div className="hidden small:block">
        <div className="small:sticky small:top-24">
          <div className="py-1">
            {renderFilterControls()}

            {hasCategories && (
              <div className="mt-5 border-t border-black/10 pt-5">
                <Text className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/40">
                  Explorar
                </Text>
                <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
                  {categoryLinks.map((category) => (
                    <li key={category.id}>
                      <LocalizedClientLink
                        href={category.path}
                        className="font-lealti-light text-xs tracking-[0.08em] text-black/55 transition hover:text-black"
                      >
                        {category.name}
                      </LocalizedClientLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="small:hidden w-full border-b border-black/10 pb-4">
        <div className="flex items-center justify-between pt-2">
          <Text className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/50">
            Filtros {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
          </Text>
          <Button
            variant="secondary"
            size="small"
            onClick={filterDrawer.open}
            data-testid="mobile-filter-button"
            className="h-8 rounded-none border border-black/30 bg-white px-3 font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black transition hover:border-black"
          >
            Filtrar
          </Button>
        </div>

        {hasCategories && (
          <div className="mt-3 overflow-x-auto pb-1">
            <ul className="flex gap-3">
              {categoryLinks.map((category) => (
                <li key={category.id}>
                  <LocalizedClientLink
                    href={category.path}
                    className="whitespace-nowrap font-lealti-light text-[10px] uppercase tracking-[0.18em] text-black/50 transition hover:text-black"
                  >
                    {category.name}
                  </LocalizedClientLink>
                </li>
              ))}
            </ul>
          </div>
        )}

        <MobileFilterDrawer
          isOpen={filterDrawer.state}
          onClose={filterDrawer.close}
          categoryLinks={categoryLinks}
          hasCategories={hasCategories}
          renderFilters={() => renderFilterControls(filterDrawer.close)}
        />
      </div>
    </div>
  )
}

export default RefinementList

type CategoryLink = {
  id: string
  name: string
  path: string
}

type MobileFilterDrawerProps = {
  isOpen: boolean
  onClose: () => void
  categoryLinks: CategoryLink[]
  hasCategories: boolean
  renderFilters: () => ReactElement
}

const MobileFilterDrawer = ({
  isOpen,
  onClose,
  categoryLinks,
  hasCategories,
  renderFilters,
}: MobileFilterDrawerProps) => {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[75]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="flex h-full w-screen max-w-sm flex-col bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-black/10 px-5 py-5">
                <Text className="font-lealti text-[11px] uppercase tracking-[0.3em] text-black">
                  Filtros
                </Text>
                <button
                  onClick={onClose}
                  className="flex h-8 w-8 items-center justify-center text-black/40 transition hover:text-black"
                  aria-label="Cerrar filtros"
                >
                  <XMark className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-5">
                {renderFilters()}

                {hasCategories && (
                  <div className="mt-5 border-t border-black/10 pt-5">
                    <Text className="font-lealti-light text-[10px] uppercase tracking-[0.2em] text-black/40">
                      Categorías
                    </Text>
                    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                      {categoryLinks.map((category) => (
                        <li key={category.id}>
                          <LocalizedClientLink
                            href={category.path}
                            onClick={onClose}
                            className="font-lealti-light text-[11px] uppercase tracking-[0.12em] text-black/55 transition hover:text-black"
                          >
                            {category.name}
                          </LocalizedClientLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
